import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Lightning, X, List, SignOut, User } from '@phosphor-icons/react';

const navStyle = { fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '22.2px', lineHeight: 1.75, textTransform: 'uppercase' };

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { label: 'About', path: '/about' },
    { label: 'Products', path: '/products' },
    { label: 'Services', path: '/services' },
    { label: 'Contact Us', path: '/contact' },
  ];

  const isHome = location.pathname === '/';
  const navBg = scrolled || !isHome
    ? 'bg-white border-b border-slate-200 shadow-sm'
    : 'bg-[#0B132B]/95 backdrop-blur-md';
  const textColor = scrolled || !isHome ? 'text-[#0B132B]' : 'text-white';
  const hoverColor = scrolled || !isHome ? 'hover:text-[#0052FF]' : 'hover:text-[#10B981]';
  const activeColor = 'text-[#0052FF]';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`} data-testid="header-nav">
      <div className="w-full px-6 lg:px-12 xl:px-[8%]">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link to="/" className={`flex items-center gap-2 ${textColor}`} data-testid="logo-link">
            <Lightning size={32} weight="fill" className="text-[#0052FF]" />
            <span className="font-black text-2xl tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              AIAURORA<span className="text-[#10B981]">.</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                style={navStyle}
                className={`transition-colors duration-200 ${
                  location.pathname === link.path || location.pathname.startsWith(link.path + '/')
                    ? activeColor
                    : `${textColor} ${hoverColor}`
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="hidden lg:flex items-center gap-4">
            {user && user.role === 'admin' && (
              <Link to="/admin" style={navStyle} className={`transition-colors duration-200 ${textColor} ${hoverColor}`} data-testid="nav-admin-link">
                ADMIN
              </Link>
            )}
            {user ? (
              <div className="flex items-center gap-3">
                <span style={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '22.2px' }} className={`${textColor} opacity-80`}>
                  <User size={16} weight="bold" className="inline mr-1" />
                  {user.name || user.email}
                </span>
                <button
                  onClick={handleLogout}
                  style={navStyle}
                  className="flex items-center gap-2 px-6 py-2 rounded-full border-2 border-[#0052FF] text-[#0052FF] hover:bg-[#0052FF] hover:text-white transition-colors duration-200"
                  data-testid="nav-logout-btn"
                >
                  <SignOut size={16} weight="bold" />
                  Logout
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                style={navStyle}
                className="px-6 py-2 rounded-full border-2 border-[#0052FF] text-[#0052FF] hover:bg-[#0052FF] hover:text-white transition-colors duration-200"
                data-testid="nav-login-btn"
              >
                Client Portal
              </Link>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            className={`lg:hidden ${textColor}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={28} /> : <List size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-200 shadow-lg" data-testid="mobile-menu-panel">
          <div className="px-6 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 text-[16px] font-semibold ${
                  location.pathname === link.path ? 'text-[#0052FF]' : 'text-[#0B132B]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-slate-100">
              {user && user.role === 'admin' && (
                <Link to="/admin" className="block py-3 text-[16px] font-semibold text-[#0B132B]">Admin</Link>
              )}
              {user ? (
                <button onClick={handleLogout} className="block w-full text-left py-3 text-[16px] font-semibold text-red-600">Logout</button>
              ) : (
                <Link to="/login" className="block py-3 text-[16px] font-semibold text-[#0052FF]">Client Portal</Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
